import './audit-campaign-featured-list.scss'

import { first, pluck, tap } from 'rxjs/operators'
import _ from 'lodash'
import Component from 'vue-class-component'
import { Watch } from 'vue-property-decorator'

import { AuditCampaignFeatured, SearchParams } from '@skinsoft/types'
import { map } from '@skinsoft/data-store'
import { AliasDataStore as CollaboratorDataStore, AuditCampaignDataStore } from '@skinsoft/entity'
import { toCampaignFeatured$ } from '@skinsoft/entity-ui'
import { ReactiveVue } from '@skinsoft/reactive-vue'
import { NB_CAMPAIGNS_SEARCH_RESULT_CHANGED_TOPIC } from '../../../../busTopics'
import { AuditCampaignFeaturedCard } from '../audit-campaign-featured-card'
import { AuthService } from '@skinsoft/auth'

const AUDIT_CAMPAIGN_FEATURED_ITEM_COUNT = 5

@Component({
    components: {
        sAuditCampaignFeaturedCard: AuditCampaignFeaturedCard
    },
    props: ['listState', 'searchParams', 'emptyMessage', 'clickAction'],
    template: require('./audit-campaign-featured-list.html')
})
export class AuditCampaignFeaturedList extends ReactiveVue {

    public searchParams: SearchParams;
    private totalResults: number = 0;
    private campaignFeatureds: AuditCampaignFeatured[] = [];

    private clickAction: string

    private $auditCampaignDataStore: AuditCampaignDataStore;
    private $collaboratorDataStore: CollaboratorDataStore;

    public $authService: AuthService

    private async subscribe() {
        const locale = this.getAppLocale()
        const sortObject =
            this.searchParams?.sortType?.length > 0
                ? { entityFields: this.searchParams.sortType, sortAsc: this.searchParams.sortAsc }
                : null
        
        let userId = await localStorage.getItem("userId")

        let collaboratorId: string | undefined = undefined
        if(userId) {
            // Get current user associated collaborator
            collaboratorId = (await this.$collaboratorDataStore.getCollaboratorByUserId(userId).pipe(
                    first()
                ).toPromise()
            ).id            

            this.subscribeToCampaignFeatured(userId, collaboratorId ?? undefined)
            
        } else {
            this.$authService.waitForUser$().subscribe(async userAuthResult => {
                if(userAuthResult?.user?.id) {
                    userId = userAuthResult.user.id
                    localStorage.setItem("userId", userId)
                    
                    // Get current user associated collaborator
                    collaboratorId = (await this.$collaboratorDataStore.getCollaboratorByUserId(userId).pipe(
                            first()
                        ).toPromise()
                    ).id
                    
                    this.subscribeToCampaignFeatured(userId, collaboratorId ?? undefined)
                }
            })
        }
    }

    private subscribeToCampaignFeatured(userId: string, collaboratorId?: string) {
        const sortObject =
            this.searchParams?.sortType?.length > 0
                ? { entityFields: this.searchParams.sortType, sortAsc: this.searchParams.sortAsc }
                : null

        // campaign workflow status equals to 'inProgress'
        const inProgressStatusCriteria = {
            type: "requestSearch",
            leftCriteria: {
                operator: "equals", 
                value: { 
                    "text": "ui.lifecycle-state.inProgress", 
                    type: "text" 
                }, 
                entityField: "5e2713835111f87e825ede00", 
                type: "criteria"                
            },
            linkOperator: 'OR',
            rightCriteria: {
                operator: "equals", 
                value: { 
                    "text": "inProgress", 
                    type: "text" 
                }, 
                entityField: "5e2713835111f87e825ede00", 
                type: "criteria"
            },
        }
        
        let leftCriteria
        if(navigator.onLine) {
            leftCriteria = inProgressStatusCriteria
        } else {
            let offlineInProgressStatusCriteria = _.cloneDeep(inProgressStatusCriteria)
            offlineInProgressStatusCriteria.leftCriteria.entityField = "_auditProjectCurrentState"
            offlineInProgressStatusCriteria.rightCriteria.entityField = "_auditProjectCurrentState"

            leftCriteria = {
                type: "requestSearch",
                leftCriteria: inProgressStatusCriteria,
                linkOperator: 'OR',
                rightCriteria: offlineInProgressStatusCriteria,
            }
        }

        this.subscribeTo(
            this.$auditCampaignDataStore
                .searchAll({
                    queryText: this.searchParams.queryText,
                    sortObject: sortObject,
                    pageSize: this.searchParams.nbResults,
                    types: ['auditProject'],
                    page: this.searchParams.startAt,
                    // advancedSearch filtering field (on status)
                    advancedSearches: [{
                        // campaign workflow status equals to 'inProgress'
                        leftCriteria: leftCriteria,
                        linkOperator: collaboratorId ? "AND" : null,
                        // authorizedAuditor equals to current collaborator
                        rightCriteria: collaboratorId ? 
                            { 
                                operator: "equals", 
                                value: { completeSearch: false, refId: collaboratorId, type: "ref" }, 
                                entityField: "5e2713835111f87e825ede23", 
                                type: "criteria" 
                            } 
                            : null,
                        type: "requestSearch",
                    }],
                })
                .pipe(
                    tap(results => {
                        this.totalResults = results.totalSize
                    }),
                    pluck('allData'),
                    map((value) => {
                        return toCampaignFeatured$(
                            value, 
                            this.getAppLocale()
                        )
                    })
                )
            ,
            (campaignFeatureds: AuditCampaignFeatured[]) => {
                this.campaignFeatureds = campaignFeatureds;

                this.emitOnBus(NB_CAMPAIGNS_SEARCH_RESULT_CHANGED_TOPIC, { totalResults: this.totalResults })
            }
            , 'campaignFeatureds'
        )
    }

    private unsubscribe() {
        this.unsubscribeFrom('campaignFeatureds');
    }


    @Watch('searchParams', { immediate: true, deep: true })
    private onSearchParamsChange() {
        // Testing all search params presence to avoid useless search query during params setting
        if (this.searchParams.nbResults && this.searchParams.sortType?.length) {
            this.unsubscribe();
            this.campaignFeatureds = Array(AUDIT_CAMPAIGN_FEATURED_ITEM_COUNT).fill(null);
            this.$emit('totalCount', null);
            this.subscribe();
        }
    }

}